/**
 * English: Bookings
 *
 * What this file covers: The Bookings menu: calendar, bookings, services, staff and hours, classes, booking
 * settings, calendar sync.
 *
 * Tone: Friendly and clear, for small business owners. Times and dates read the way the language says them.
 * Words the client sees (confirmations, reminders) sound polite and personal.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const bookings: Catalog = {
  'bookings.title': 'Bookings',
  'bookings.readOnly': 'Only owners and admins can change this. You can see everything and make, move and cancel bookings.',
  'bookings.reasonHint': 'Optional. The client sees it when they are told.',
  'bookings.discard.title': 'Discard your changes?',
  'bookings.discard.body': 'What you changed here has not been saved.',
  'bookings.limit.seePlans': 'See plans',
  'bookings.colour.option': 'Colour {{n}}',
  'bookings.error.SLOT_TAKEN': 'That time has just been taken. Pick another.',
  'bookings.error.FULL': 'There are no places left. Pick another time.',
  'bookings.error.NOT_BOOKABLE': 'That time cannot be booked: it is outside the hours, too soon or too far ahead, or the service is switched off.',
  'bookings.error.TOO_LATE': 'It is too close to the start to change this booking.',
  'bookings.error.PLAN_LIMIT_REACHED': 'Your plan allows no more. Upgrade your plan or switch one off first.',
  'bookings.error.forbidden': 'Only owners and admins can do this.',
  'bookings.error.unavailable': 'Bookings are not available right now. Try again in a minute.',
  'bookings.status.requested': 'Waiting for approval',
  'bookings.status.confirmed': 'Confirmed',
  'bookings.status.declined': 'Declined',
  'bookings.status.cancelled': 'Cancelled',
  'bookings.status.completed': 'Completed',
  'bookings.status.no_show': 'Did not arrive',
  'bookings.channel.console': 'Booked here',
  'bookings.channel.whatsapp': 'WhatsApp',
  'bookings.channel.web': 'Website chat',
  'bookings.channel.email': 'Email',
  'bookings.channel.sms': 'SMS',
  'bookings.channel.messenger': 'Messenger',
  'bookings.channel.instagram': 'Instagram',
  'bookings.channel.voice': 'Phone call',
  'bookings.channel.ussd': 'USSD',
  'bookings.channel.public': 'Booking page',
  'bookings.source.console': 'A team member',
  'bookings.source.flow': 'The chatbot',
  'bookings.source.voice': 'The phone assistant',
  'bookings.source.inbox': 'The inbox',
  'bookings.source.tickets': 'A ticket',
  'bookings.source.public': 'The booking page',
  'bookings.history.created': 'Booked',
  'bookings.history.requested': 'Asked for',
  'bookings.history.confirmed': 'Confirmed',
  'bookings.history.approved': 'Approved',
  'bookings.history.declined': 'Declined',
  'bookings.history.moved': 'Moved',
  'bookings.history.cancelled': 'Cancelled',
  'bookings.history.completed': 'Completed',
  'bookings.history.no_show': 'Marked as did not arrive',
  'bookings.history.note': 'Note added',
  'bookings.history.reminder_sent': 'Reminder sent',
  'bookings.history.client_confirmed': 'Client confirmed',
  'bookings.kind.appointment': 'Appointment',
  'bookings.kind.class': 'Class or event',
  'bookings.kind.callout': 'Call-out',
  'bookings.location.business': 'At the business',
  'bookings.location.client': 'At the client\'s address',
  'bookings.location.online': 'Online',
  'bookings.questionType.text': 'Short answer',
  'bookings.questionType.number': 'Number',
  'bookings.questionType.date': 'Date',
  'bookings.questionType.select': 'Pick from a list',
  'bookings.questionType.yes_no': 'Yes or no',
  'bookings.questionType.address': 'Address',
  'bookings.field.number': 'Booking number',
  'bookings.field.when': 'When',
  'bookings.field.client': 'Client',
  'bookings.field.clientName': 'Client\'s name',
  'bookings.field.phone': 'Phone',
  'bookings.field.service': 'Service',
  'bookings.field.person': 'Person',
  'bookings.field.address': 'Address',
  'bookings.field.answers': 'Answers',
  'bookings.field.notes': 'Notes',
  'bookings.field.channel': 'Channel',
  'bookings.field.source': 'Booked by',
  'bookings.field.reason': 'Reason',
  'bookings.field.notifyClient': 'Tell the client',
  'bookings.action.approve': 'Approve',
  'bookings.action.decline': 'Decline',
  'bookings.action.move': 'Move',
  'bookings.action.cancel': 'Cancel booking',
  'bookings.action.complete': 'Mark completed',
  'bookings.action.noShow': 'Did not arrive',
  'bookings.toast.approved': '{{number}} is approved and the client has been told.',
  'bookings.toast.declined': '{{number}} is declined.',
  'bookings.toast.moved': '{{number}} is moved.',
  'bookings.toast.cancelled': '{{number}} is cancelled.',
  'bookings.toast.completed': '{{number}} is marked completed.',
  'bookings.toast.noShow': '{{number}} is marked as did not arrive.',
  'bookings.toast.noteAdded': 'Note added to {{number}}.',
  'bookings.cancel.title': 'Cancel {{number}}?',
  'bookings.cancel.body': '{{name}}\'s booking on {{when}} will be cancelled and the time freed.',
  'bookings.cancel.confirm': 'Cancel booking',
  'bookings.decline.title': 'Decline {{number}}?',
  'bookings.decline.body': '{{name}} asked for {{when}}. Declining tells them it could not be booked.',
  'bookings.decline.confirm': 'Decline',
  'bookings.move.title': 'Move {{number}}',
  'bookings.move.now': 'Booked now for {{when}}.',
  'bookings.move.notifyHint': 'Sends the new time on the channel the booking came from, and by email when there is an address.',
  'bookings.move.confirm': 'Move to this time',
  'bookings.drawer.title': 'Booking {{number}}',
  'bookings.drawer.loading': 'Booking',
  'bookings.drawer.when': '{{start}} to {{end}}',
  'bookings.drawer.with': 'With {{name}}',
  'bookings.drawer.openConversation': 'Open the conversation',
  'bookings.drawer.openContact': 'Open the contact',
  'bookings.drawer.addNote': 'Add a note',
  'bookings.drawer.addNoteHint': 'For your team only. The client never sees notes.',
  'bookings.drawer.saveNote': 'Save note',
  'bookings.drawer.history': 'History',
  'bookings.drawer.noHistory': 'Nothing has happened to this booking yet.',
  'bookings.picker.day': 'Day',
  'bookings.picker.time': 'Time',
  'bookings.picker.session': 'Session',
  'bookings.picker.windowLabel': 'Arrival window',
  'bookings.picker.window': '{{from}} to {{to}}',
  'bookings.picker.placesLeft': '{{count}} left',
  'bookings.picker.earlier': 'Earlier',
  'bookings.picker.later': 'Later',
  'bookings.picker.noneThisWeek': 'Nothing free in these days. Try later days.',
  'bookings.picker.noneThisDay': 'Nothing free on this day. Pick another.',
  'bookings.picker.chosen': 'Chosen: {{when}}',
  'bookings.new.title': 'New booking',
  'bookings.new.chooseService': 'Choose a service',
  'bookings.new.serviceRequired': 'Choose the service being booked.',
  'bookings.new.anyone': 'Whoever is free',
  'bookings.new.personHint': 'Leave it on whoever is free to see every free time.',
  'bookings.new.timeRequired': 'Choose a time.',
  'bookings.new.findContact': 'Find a contact',
  'bookings.new.findContactHint': 'Type a name, number or email address to fill in the client from your contacts.',
  'bookings.new.nameRequired': 'Enter the client\'s name.',
  'bookings.new.phoneHint': 'With the country code, such as +27 82 123 4567.',
  'bookings.new.reachRequired': 'Enter a phone number or an email address, so the client can be told.',
  'bookings.new.addressRequired': 'Enter the address to go to.',
  'bookings.new.answerRequired': 'This question needs an answer.',
  'bookings.new.notesHint': 'For your team only.',
  'bookings.new.notifyHint': 'Sends the confirmation now, and reminders before the time.',
  'bookings.new.submit': 'Book',
  'bookings.new.noServices': 'There are no services to book yet.',
  'bookings.new.doneTitle': 'Booked',
  'bookings.new.doneConfirmed': 'The booking is confirmed.',
  'bookings.new.doneRequested': 'The booking is waiting for approval.',
  'bookings.new.openBooking': 'Open the booking',
  'bookings.calendar.title': 'Calendar',
  'bookings.calendar.description': 'Every booking by day and time. Click an empty time to book it.',
  'bookings.calendar.today': 'Today',
  'bookings.calendar.previous': 'Earlier',
  'bookings.calendar.next': 'Later',
  'bookings.calendar.week': 'Week',
  'bookings.calendar.day': 'Day',
  'bookings.calendar.weekOf': '{{from}} to {{to}}',
  'bookings.calendar.everyone': 'Everyone',
  'bookings.calendar.showCancelled': 'Show cancelled',
  'bookings.calendar.clickToBook': 'Click to book this time',
  'bookings.calendar.blockLabel': '{{time}}, {{service}}, {{client}}, {{status}}',
  'bookings.calendar.zone': 'Times are in {{zone}}.',
  'bookings.calendar.empty': 'No bookings in these days.',
  'bookings.list.title': 'Bookings',
  'bookings.list.description': 'Every booking, with filters to find one. Approve or decline requests from here.',
  'bookings.list.search': 'Search by name, number or booking number',
  'bookings.list.anyStatus': 'Any status',
  'bookings.list.anyService': 'Any service',
  'bookings.list.from': 'From',
  'bookings.list.to': 'To',
  'bookings.list.empty': 'No bookings yet',
  'bookings.list.emptyHint': 'Bookings made here, by the chatbot or on your booking page show up in this list.',
  'bookings.list.emptyFiltered': 'No bookings match these filters',
  'bookings.list.emptyFilteredHint': 'Clear the filters to see every booking from today on.',
  'bookings.list.approveFor': 'Approve {{number}}',
  'bookings.list.declineFor': 'Decline {{number}}',
  'bookings.services.title': 'Services',
  'bookings.services.description': 'What clients can book: appointments with a person, classes and events, and call-outs to their address.',
  'bookings.services.new': 'Add a service',
  'bookings.services.addFirst': 'Add your first service',
  'bookings.services.create': 'Add service',
  'bookings.services.emptyTitle': 'No services yet',
  'bookings.services.emptyHint': 'Add what clients book, such as a haircut, a yoga class or a plumber\'s call-out.',
  'bookings.services.noneActive': 'No services are switched on',
  'bookings.services.noneActiveHint': 'Show the switched-off ones to turn one back on.',
  'bookings.services.showInactive': 'Show switched off',
  'bookings.services.countOfLimit': '{{count}} of {{limit}}',
  'bookings.services.limitReached': 'Your plan allows {{limit}} services. Switch one off or upgrade your plan to add another.',
  'bookings.services.on': 'On',
  'bookings.services.off': 'Off',
  'bookings.services.nobody': 'Nobody does this yet',
  'bookings.services.minutes': '{{n}} min',
  'bookings.services.windowsCount': '{{count}} windows',
  'bookings.services.remove.title': 'Remove {{name}}?',
  'bookings.services.remove.body': 'Clients can no longer book it. Bookings already made stay as they are.',
  'bookings.services.toast.created': '{{name}} is added.',
  'bookings.services.toast.saved': '{{name}} is saved.',
  'bookings.services.toast.removed': '{{name}} is removed.',
  'bookings.services.descriptionHint': 'Optional. Clients see it when they choose a service.',
  'bookings.services.kind': 'Kind',
  'bookings.services.kindHint.appointment': 'A set length of time, with one of your people or with nobody in particular, such as a consultation or a car wash.',
  'bookings.services.kindHint.class': 'Many clients at a set time, such as a class, a workshop or a tour.',
  'bookings.services.kindHint.callout': 'You go to the client within an arrival window, such as a repair or a delivery.',
  'bookings.services.kindLocked': 'The kind cannot be changed once the service is made.',
  'bookings.services.duration': 'Length',
  'bookings.services.minutesHint': 'In minutes.',
  'bookings.services.bufferBefore': 'Time before',
  'bookings.services.bufferAfter': 'Time after',
  'bookings.services.bufferHint': 'Minutes kept free around each booking, to prepare or tidy up.',
  'bookings.services.capacity': 'Places',
  'bookings.services.capacityHint': 'How many clients fit in one session. Each session can change it.',
  'bookings.services.windows': 'Arrival windows',
  'bookings.services.windowFrom': 'From',
  'bookings.services.windowTo': 'To',
  'bookings.services.windowCapacity': 'Call-outs',
  'bookings.services.windowsHint': 'Each working day offers these windows. Call-outs is how many visits fit in one window.',
  'bookings.services.addWindow': 'Add a window',
  'bookings.services.removeWindow': 'Remove window {{n}}',
  'bookings.services.location': 'Where',
  'bookings.services.locationHint': 'Clients are told this in the confirmation.',
  'bookings.services.onlineLink': 'Meeting link',
  'bookings.services.onlineLinkHint': 'Sent to the client with the confirmation. Starts with https://.',
  'bookings.services.price': 'Price',
  'bookings.services.priceHint': 'Optional. Shown as you write it, such as R350 or From R200. Nothing is charged.',
  'bookings.services.colour': 'Colour on the calendar',
  'bookings.services.approval': 'Approval',
  'bookings.services.approval.default': 'Use the default',
  'bookings.services.approval.always': 'Always ask first',
  'bookings.services.approval.never': 'Confirm straight away',
  'bookings.services.approvalHint': 'When you ask first, a booking waits until someone approves it. The default is set in Booking settings.',
  'bookings.services.people': 'People who do it',
  'bookings.services.peopleHint': 'Only these people\'s times are offered for this service.',
  'bookings.services.noPeople': 'Add people under Staff and hours first.',
  'bookings.services.staffMode': 'Who the client gets',
  'bookings.services.staffMode.any': 'Whoever is free',
  'bookings.services.staffMode.choose': 'The client chooses',
  'bookings.services.staffModeHint': 'Whoever is free shares bookings out evenly among the people who do it.',
  'bookings.services.questions': 'Questions',
  'bookings.services.questionsHint': 'Asked when a client books, in the chat and on the booking page.',
  'bookings.services.questionLabel': 'Question',
  'bookings.services.questionKey': 'Saved as {{key}}',
  'bookings.services.options': 'Choices',
  'bookings.services.optionsHint': 'At least two, separated by commas.',
  'bookings.services.addQuestion': 'Add a question',
  'bookings.services.removeQuestion': 'Remove the question {{label}}',
  'bookings.services.sortOrder': 'Order',
  'bookings.services.sortOrderHint': 'Lower numbers are listed first.',
  'bookings.services.active': 'Clients can book it',
  'bookings.services.activeHint': 'Switch off to stop new bookings without removing it.',
  'bookings.services.error.name': 'Give the service a name.',
  'bookings.services.error.duration': 'The length must be at least 5 minutes.',
  'bookings.services.error.capacity': 'A class needs at least one place.',
  'bookings.services.error.link': 'The link must start with http:// or https://.',
  'bookings.services.error.noWindows': 'Add at least one arrival window.',
  'bookings.services.error.windowTimes': 'Each window must end after it starts.',
  'bookings.services.error.windowCapacity': 'Each window needs room for at least one call-out.',
  'bookings.services.error.questionLabel': 'Every question needs its wording.',
  'bookings.services.error.options': 'A question picked from a list needs at least two choices.',
  'bookings.staff.title': 'Staff and hours',
  'bookings.staff.description': 'The people clients book, when each one works and when they are away.',
  'bookings.staff.new': 'Add a person',
  'bookings.staff.addFirst': 'Add the people clients book',
  'bookings.staff.create': 'Add person',
  'bookings.staff.emptyTitle': 'No people yet',
  'bookings.staff.emptyHint': 'Add each person clients can book, even if it is only you. They do not need to sign in.',
  'bookings.staff.countOfLimit': '{{count}} of {{limit}}',
  'bookings.staff.limitReached': 'Your plan allows {{limit}} people. Switch one off or upgrade your plan to add another.',
  'bookings.staff.on': 'Bookable',
  'bookings.staff.off': 'Not bookable',
  'bookings.staff.nameHint': 'As clients see it, such as Thandi or Dr Naidoo.',
  'bookings.staff.teamMember': 'Team member',
  'bookings.staff.noTeamMember': 'Not a team member',
  'bookings.staff.teamMemberHint': 'Optional. A linked team member is told about their own bookings.',
  'bookings.staff.colour': 'Colour on the calendar',
  'bookings.staff.hours': 'Working hours',
  'bookings.staff.hours.business': 'Same as the business',
  'bookings.staff.hours.own': 'Own hours',
  'bookings.staff.hours.businessHint': 'This person is bookable whenever the business is open.',
  'bookings.staff.hours.businessLink': 'Change the business hours',
  'bookings.staff.active': 'Clients can book this person',
  'bookings.staff.activeHint': 'Switch off while someone is away for a long time, or has left.',
  'bookings.staff.error.name': 'Enter the person\'s name.',
  'bookings.staff.error.email': 'That email address does not look right.',
  'bookings.staff.remove.title': 'Remove {{name}}?',
  'bookings.staff.remove.body': 'Clients can no longer book this person. Bookings already made stay as they are.',
  'bookings.staff.toast.created': '{{name}} is added.',
  'bookings.staff.toast.saved': '{{name}} is saved.',
  'bookings.staff.toast.removed': '{{name}} is removed.',
  'bookings.timeOff.title': 'Time off',
  'bookings.timeOff.hint': 'Leave, training or anything else when this person cannot be booked.',
  'bookings.timeOff.empty': 'No time off coming up.',
  'bookings.timeOff.from': 'From',
  'bookings.timeOff.to': 'To',
  'bookings.timeOff.reason': 'Reason',
  'bookings.timeOff.reasonHint': 'Optional. Only your team sees it.',
  'bookings.timeOff.add': 'Add time off',
  'bookings.timeOff.range': '{{from}} to {{to}}',
  'bookings.timeOff.removeFor': 'Remove the time off from {{from}}',
  'bookings.timeOff.error.times': 'Enter when the time off starts and ends.',
  'bookings.timeOff.error.order': 'The end must be after the start.',
  'bookings.timeOff.toast.added': 'Time off added for {{name}}.',
  'bookings.classes.title': 'Classes',
  'bookings.classes.description': 'Sessions of your classes and events, week by week.',
  'bookings.classes.new': 'Add a session',
  'bookings.classes.edit': 'Change the session',
  'bookings.classes.create': 'Add',
  'bookings.classes.allClasses': 'All classes',
  'bookings.classes.noClassServices': 'No classes or events yet',
  'bookings.classes.noClassServicesHint': 'Add a service of the kind Class or event first, then give it sessions here.',
  'bookings.classes.toServices': 'Go to services',
  'bookings.classes.emptyWeek': 'No sessions this week',
  'bookings.classes.emptyWeekHint': 'Add one session, or a series that repeats every week.',
  'bookings.classes.sessionLine': '{{from}} to {{to}}, {{name}}',
  'bookings.classes.booked': '{{booked}} of {{capacity}} booked',
  'bookings.classes.full': 'Full',
  'bookings.classes.repeats': 'Repeats',
  'bookings.classes.cancel': 'Cancel session',
  'bookings.classes.date': 'Date',
  'bookings.classes.time': 'Starts at',
  'bookings.classes.noPerson': 'Nobody in particular',
  'bookings.classes.capacityHint': '{{booked}} already booked.',
  'bookings.classes.repeat': 'Repeat every week',
  'bookings.classes.repeatHint': 'Makes a session on each chosen day at this time.',
  'bookings.classes.weekdays': 'Days',
  'bookings.classes.until': 'Until',
  'bookings.classes.untilHint': 'The last day of the series.',
  'bookings.classes.error.until': 'The last day must be on or after the first.',
  'bookings.classes.cancelTitle': 'Cancel this session of {{name}}?',
  'bookings.classes.cancelBody': 'The session on {{when}} at {{time}} is cancelled, and so are its {{count}} bookings.',
  'bookings.classes.notifyClients': 'Tell the clients',
  'bookings.classes.toast.created': '{{count}} sessions added.',
  'bookings.classes.toast.saved': 'The session is saved.',
  'bookings.classes.toast.cancelled': 'The session of {{name}} is cancelled.',
  'bookings.settings.title': 'Booking settings',
  'bookings.settings.description': 'How far ahead clients may book, reminders, confirmations and your public booking page.',
  'bookings.settings.times': 'Booking times',
  'bookings.settings.slotStep': 'Start times every',
  'bookings.settings.slotStepHint': 'How far apart the appointment times offered to clients are.',
  'bookings.settings.minNotice': 'Least notice, in hours',
  'bookings.settings.minNoticeHint': 'A client cannot book a time sooner than this.',
  'bookings.settings.horizon': 'Furthest ahead, in days',
  'bookings.settings.horizonHint': 'A client cannot book further ahead than this.',
  'bookings.settings.cutoff': 'Changes allowed until, in hours before',
  'bookings.settings.cutoffHint': 'Inside this many hours of the start, a client cannot move or cancel by themselves.',
  'bookings.settings.approvalDefault': 'Ask before confirming bookings',
  'bookings.settings.approvalDefaultHint': 'The default for services set to Use the default. Each service can say otherwise.',
  'bookings.settings.reminders': 'Reminders',
  'bookings.settings.remindersIntro': 'Clients get a reminder with Confirm, Reschedule and Cancel buttons.',
  'bookings.settings.noReminders': 'No reminders are sent.',
  'bookings.settings.hoursBefore': 'Hours before',
  'bookings.settings.remindersHint': '24 is a day before. Up to five reminders.',
  'bookings.settings.addReminder': 'Add a reminder',
  'bookings.settings.removeReminder': 'Remove reminder {{n}}',
  'bookings.settings.confirmations': 'Confirmations',
  'bookings.settings.confirmationsIntro': 'How clients are told about a booking, a change and a reminder.',
  'bookings.settings.confirmWhatsapp': 'By WhatsApp',
  'bookings.settings.confirmWhatsappHint': 'When the client booked on WhatsApp, or has a WhatsApp number.',
  'bookings.settings.confirmEmail': 'By email',
  'bookings.settings.confirmEmailHint': 'Whenever there is an email address, with a calendar file attached.',
  'bookings.settings.confirmSms': 'By SMS',
  'bookings.settings.confirmSmsHint': 'Charged from your credits like any SMS.',
  'bookings.settings.publicPage': 'Public booking page',
  'bookings.settings.publicPageIntro': 'A page where anyone can book without a chat. Share its address on your website or social media.',
  'bookings.settings.publicEnabled': 'The booking page is on',
  'bookings.settings.slug': 'Short name',
  'bookings.settings.slugHint': 'Lower-case letters, numbers and hyphens, such as smith-dental. It is the end of the page\'s address.',
  'bookings.settings.publicUrl': 'Page address',
  'bookings.settings.openPage': 'Open the page',
  'bookings.settings.publicOffHint': 'The address works once the booking page is on.',
  'bookings.settings.address': 'Business address',
  'bookings.settings.addressHint': 'Shown on the booking page and in confirmations for services at the business.',
  'bookings.settings.prefix': 'Booking number starts with',
  'bookings.settings.prefixHint': 'Up to eight letters, numbers, hyphens or slashes, such as B-. A booking number then reads like {{example}}.',
  'bookings.settings.limits': 'Your plan',
  'bookings.settings.limitStaff': 'Up to {{n}} people clients can book.',
  'bookings.settings.limitStaffNone': 'No limit on people clients can book.',
  'bookings.settings.limitServices': 'Up to {{n}} services.',
  'bookings.settings.limitServicesNone': 'No limit on services.',
  'bookings.settings.limitsHint': 'A bigger plan allows more.',
  'bookings.settings.fixFirst': 'Fix the marked fields first.',
  'bookings.settings.error.reminder': 'Each reminder must be more than 0 and at most 336 hours before.',
  'bookings.settings.error.reminderDuplicate': 'Two reminders are at the same time.',
  'bookings.settings.error.slug': 'Use 3 to 40 lower-case letters, numbers and hyphens, not starting or ending with a hyphen.',
  'bookings.settings.error.prefix': 'Use up to eight letters, numbers, hyphens or slashes.',
  'bookings.settings.toast.saved': 'Booking settings are saved.',
  'bookings.calendar.business': 'Business calendar (every booking)',
  'bookings.calendar.column': 'Calendar',
  'bookings.calendar.none': 'Not connected',
  'bookings.calendar.notYet': 'Not available yet',
  'bookings.calendar.notOnPlan': 'Calendar sync is not on your plan. It is included from Plus.',
  'bookings.calendar.connect.google': 'Connect Google',
  'bookings.calendar.connect.microsoft': 'Connect Outlook',
  'bookings.calendar.provider.google': 'Google Calendar',
  'bookings.calendar.provider.microsoft': 'Outlook calendar',
  'bookings.calendar.status.error': 'Needs connecting again',
  'bookings.calendar.reconnect': 'Connect again',
  'bookings.calendar.disconnect': 'Disconnect',
  'bookings.calendar.disconnect.title': 'Disconnect this calendar?',
  'bookings.calendar.disconnect.body': 'Upcoming bookings are taken out of the calendar, and its busy times no longer block bookings. The bookings themselves stay as they are.',
  'bookings.calendar.toast.connected': '{{account}} is connected. Bookings will appear in it within a minute.',
  'bookings.calendar.toast.cancelled': 'The calendar was not connected.',
  'bookings.calendar.toast.disconnected': 'The calendar is disconnected.',
  'bookings.calendar.error.generic': 'The calendar could not be connected. Please try again.',
  'bookings.calendar.error.CALENDAR_SYNC_UNAVAILABLE': 'Calendar sync is not available yet.',
  'bookings.calendar.error.CALENDAR_PROVIDER_NOT_CONFIGURED': 'Connecting this kind of calendar is not available yet.',
  'bookings.calendar.error.CALENDAR_LINK_EXPIRED': 'That link has expired. Please connect again.',
  'bookings.calendar.error.CALENDAR_NOT_YOURS': 'This connection was started by someone else. Please connect again yourself.',
  'bookings.calendar.error.CALENDAR_REFUSED': 'The calendar did not allow access. Please connect again and allow access.',
  'bookings.calendar.error.PLAN_LIMIT_REACHED': 'Calendar sync is not on your plan. It is included from Plus.',
  'bookings.calendar.sync.title': 'Calendar sync',
  'bookings.calendar.sync.description': 'Connect a Google or Outlook calendar. Bookings appear in it as events and move or disappear with them, and busy times in a person\'s calendar are not offered to clients. Events added in the calendar never become bookings.',
  'bookings.services.staffMode.none': 'Nobody in particular (a team, a bay or the business)',
  'bookings.services.staffModeNoneHint': 'Clients book a time within your business hours. The booking is not given to one person.',
  'bookings.services.atOnce': 'How many at the same time',
  'bookings.services.atOnceHint': 'How many bookings of this service can happen at once, such as 2 when two cars can be washed together.',
  'bookings.services.error.atOnce': 'Allow at least one at a time.',
  'bookings.services.noPersonAtOnce': 'Nobody in particular, {{n}} at a time',
  'bookings.settings.section.times': 'Booking times',
  'bookings.settings.section.reminders': 'Reminders',
  'bookings.settings.section.confirmations': 'Confirmations',
  'bookings.settings.section.page': 'Booking page',
  'bookings.settings.section.general': 'General'
};

export default bookings;
